import { Controller } from "stimulus";

// Warns before the user leaves a form with unsaved changes: Turbo visits get the product's confirm dialog,
// reload/close/external links get the browser's native prompt. The form is compared with a snapshot taken at the user's
// first interaction (a trusted pointerdown, keydown, beforeinput or change), so values that scripts or the browser set
// after connect never count as edits.
// Copy comes from the values; `pendingSelector` marks in-flight or unsaved uploads, which always count as unsaved.
const INTERACTION_EVENTS = ["pointerdown", "keydown", "beforeinput", "change"];

const fileSignature = (file) => (file.name || file.size ? `${file.name}:${file.size}:${file.lastModified}` : "");

export default class extends Controller {
  static values = {
    message: { type: String, default: "Your unsaved changes will be lost if you leave this page." },
    title: { type: String, default: "Leave without saving?" },
    label: { type: String, default: "Leave page" },
    pendingSelector: { type: String, default: "" },
  };

  connect() {
    this.baseline = null;
    this.skip = false;
    this.onBeforeVisit = this.onBeforeVisit.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
    this.onInteraction = this.onInteraction.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onSubmitEnd = this.onSubmitEnd.bind(this);

    document.addEventListener("turbo:before-visit", this.onBeforeVisit);
    window.addEventListener("beforeunload", this.onBeforeUnload);
    INTERACTION_EVENTS.forEach((name) => this.element.addEventListener(name, this.onInteraction, true));
    this.element.addEventListener("submit", this.onSubmit);
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd);
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    INTERACTION_EVENTS.forEach((name) => this.element.removeEventListener(name, this.onInteraction, true));
    this.element.removeEventListener("submit", this.onSubmit);
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd);
  }

  // A File is compared by name, size and date; an untouched file input (nameless, empty File) is "".
  entries() {
    return [...new FormData(this.element)].map(([key, value]) => [key, typeof value === "string" ? value : fileSignature(value)]);
  }

  get dirty() {
    if (this.pendingSelectorValue && this.element.querySelector(this.pendingSelectorValue)) return true;
    if (!this.baseline) return false;
    return new URLSearchParams(this.entries()).toString() !== new URLSearchParams(this.baseline).toString();
  }

  // Captured so it runs before the user's first edit lands; scripted events are not the user.
  onInteraction(event) {
    if (this.baseline || !event.isTrusted) return;
    this.baseline = this.entries();
  }

  // preview_controller submits the form as a GET into a turbo frame to refresh the preview; that is not a save.
  onSubmit() {
    if (this.element.method !== "get") this.skip = true;
  }

  // A failed Turbo submit leaves the user on the form with their changes still unsaved.
  onSubmitEnd(event) {
    if (!event.detail.success) this.skip = false;
  }

  onBeforeVisit(event) {
    if (this.skip || !this.dirty) return;

    const url = new URL(event.detail.url);
    // Jumping to an anchor on this page leaves nothing behind.
    if (url.hash && url.href.split("#")[0] === window.location.href.split("#")[0]) return;

    const confirm = window.Turbo.config?.forms?.confirm;
    if (!confirm) return console.warn("unsaved-changes: Turbo.config.forms.confirm is missing, not warning");

    event.preventDefault();
    // The global confirm dialog reads its title and button label from the form's data-confirm-* attributes.
    this.element.dataset.confirmTitle ||= this.titleValue;
    this.element.dataset.confirmLabel ||= this.labelValue;
    Promise.resolve(confirm(this.messageValue, this.element, null)).then((leave) => {
      if (!leave) return;
      this.skip = true;
      window.Turbo.visit(url.href);
    });
  }

  onBeforeUnload(event) {
    if (this.skip || !this.dirty) return;

    event.preventDefault();
    event.returnValue = "";
  }
}
